import { Editor } from "@shared/components/Editor";
import { InputControl, SelectControl } from "@shared/components/form";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import { Button } from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import theme from "@shared/utils/themeColors";
import EmojiPicker from "emoji-picker-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  CalendarEvent,
  CreateEventData,
  EventCategory,
  EventType,
  MediaItem,
  UpdateEventData,
} from "../types/calendar.types";
import { getUtcOffsetLabel } from "../utils/timezoneUtils";

const EVENT_TYPE_OPTIONS = [
  { value: "in-person", label: "In Person" },
  { value: "virtual", label: "Virtual" },
  { value: "hybrid", label: "Hybrid" },
];

const TIMEZONE_OPTIONS = Intl.supportedValuesOf("timeZone").map((tz) => ({
  value: tz,
  label: getUtcOffsetLabel(tz),
}));
interface EventFormProps {
  event?: CalendarEvent;
  categories: EventCategory[];
  onSubmit: (data: CreateEventData | UpdateEventData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const EventForm: React.FC<EventFormProps> = ({
  event,
  categories,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting,
  canEdit = true,
  canDelete = false,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [url, setUrl] = useState("");
  const [location, setLocation] = useState("");
  const [eventType, setEventType] = useState<EventType>("in-person");
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [error, setError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState<"title" | "description">(
    "title"
  );

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [showMediaUploader, setShowMediaUploader] = useState(false);
  const handleMediaUpload = (newMediaFiles: MediaFile[]) => {
    setMediaFiles([...mediaFiles, ...newMediaFiles]);
    setShowMediaUploader(false);
  };

  const [mediaFilesAttachments, setMediaFilesAttachments] = useState<
    MediaFile[]
  >([]);
  const [showMediaUploaderAttachments, setShowMediaUploaderAttachments] =
    useState(false);
  const handleMediaUploadAttachments = (newMediaFiles: MediaFile[]) => {
    setMediaFilesAttachments((prev) => [...prev, ...newMediaFiles]);
    setShowMediaUploaderAttachments(false);
  };

  // Initialize form with event data if editing
  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || "");

      const start = event.startDate.toDate();
      setStartDate(formatDateForInput(start));
      setStartTime(formatTimeForInput(start));

      if (event.endDate) {
        const end = event.endDate.toDate();
        setEndDate(formatDateForInput(end));
        setEndTime(formatTimeForInput(end));
      }

      setCategoryId(event.categoryId);
      setUrl(event.url || "");
      setLocation(event.location || "");
      setEventType(event.eventType || "in-person");
      setTimezone(event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      // Initialize images if they exist
      setMediaFiles(
        event.featuredImage ? mediaItemsToMediaFiles(event.featuredImage) : []
      );
      setMediaFilesAttachments(
        event.attachments ? mediaItemsToMediaFiles(event.attachments) : []
      );
    } else if (categories.length > 0) {
      // Set default category for new events
      setCategoryId(categories[0].id);
      setMediaFiles([]);
      setMediaFilesAttachments([]);
    }
  }, [event, categories]);

  const mediaItemsToMediaFiles = (mediaItems: MediaItem[]): MediaFile[] => {
    return mediaItems.map((item) => ({
      type: item.type as MediaType,
      preview: item.variants[0]?.url || "",
      uploadedUrl: item.variants[0]?.url || "",
      file: new File([], "", { type: `${item.type}/jpeg` }),
      progress: 100,
    }));
  };

  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const formatTimeForInput = (date: Date): string => {
    return date.toTimeString().slice(0, 5);
  };

  // Convert MediaFile to MediaItem for API
  const convertMediaFilesToItems = (): MediaItem[] => {
    return mediaFiles.map((file) => ({
      type: file.type as "image" | "video",
      variants: [
        {
          url: file.uploadedUrl || file.preview,
          format: file.file.type.split("/")[1] || "jpeg",
          type: "original",
        },
      ],
    }));
  };

  // Convert MediaFile to MediaItem for API
  const convertMediaFilesAttachmentsToItems = (): MediaItem[] => {
    return mediaFilesAttachments.map((file) => ({
      type: file.type as "image" | "video",
      variants: [
        {
          url: file.uploadedUrl || file.preview,
          format: file.file.type.split("/")[1] || "jpeg",
          type: "original",
        },
      ],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!title.trim()) {
      setError("Event title is required");
      return;
    }

    if (!startDate || !startTime) {
      setError("Start date and time are required");
      return;
    }

    if (!categoryId) {
      setError("Please select a category");
      return;
    }

    try {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      let endDateTime: Date | undefined;

      if (endDate && endTime) {
        endDateTime = new Date(`${endDate}T${endTime}`);

        if (endDateTime < startDateTime) {
          setError("End date/time cannot be before start date/time");
          return;
        }
      }

      const featuredImage = convertMediaFilesToItems();
      const attachments = convertMediaFilesAttachmentsToItems();
      const eventData: CreateEventData | UpdateEventData = {
        title,
        description: description || undefined,
        startDate: startDateTime,
        endDate: endDateTime,
        categoryId,
        url: url || undefined,
        featuredImage,
        attachments,
        location: location || undefined,
        eventType,
        timezone,
      };

      await onSubmit(eventData);
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error submitting event form:", err);
      setError("Failed to save event. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete();
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error deleting event:", err);
      setError("Failed to delete event. Please try again.");
    }
  };

  const handleEmojiClick = (emojiData: { emoji: string }) => {
    if (emojiTarget === "title") {
      setTitle((prev) => prev + emojiData.emoji);
    } else {
      setDescription((prev) => prev + emojiData.emoji);
    }
    setShowEmojiPicker(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onCancel}
        ></div>
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {event ? "Edit event" : "Create new event"}
            </h2>
            <button
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              type="button"
              onClick={onCancel}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Media upload controls */}
            <div className="mt-[20px] mb-4">
              <div className="flex items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Featured Image
                </label>
              </div>

              {/* Media Uploader if no image */}
              {mediaFiles.length === 0 && (
                <div className="mb-3">
                  <MediaUploader
                    onUploadComplete={handleMediaUpload}
                    onError={(error) => {
                      toast.error(error);
                    }}
                    maxFiles={1}
                    acceptType="image"
                  />
                </div>
              )}

              {/* Media preview */}
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {mediaFiles.map((file, index) => (
                    <div
                      key={index}
                      className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700"
                    >
                      {file.type === MediaType.IMAGE ? (
                        <img
                          src={file.uploadedUrl || file.preview}
                          alt=""
                          className="w-full h-auto object-cover"
                        />
                      ) : (
                        <video
                          src={file.uploadedUrl || file.preview}
                          className="w-full h-auto"
                          controls
                        />
                      )}
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                        onClick={() => {
                          setMediaFiles(
                            mediaFiles.filter((_, i) => i !== index)
                          );
                        }}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                      {file.progress < 100 && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                          <div
                            className={`h-full ${theme.bg.brand}`}
                            style={{ width: `${file.progress}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <div className="flex">
                <div className="flex-grow">
                  <InputControl
                    id="event-title"
                    label="Event Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter event title"
                    disabled={isSubmitting}
                    error={
                      error && !title.trim() ? "Event title is required" : null
                    }
                    required
                  />
                </div>
                <button
                  type="button"
                  className="ml-2 p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  onClick={() => {
                    setEmojiTarget("title");
                    setShowEmojiPicker(!showEmojiPicker);
                  }}
                >
                  <svg
                    className={`${theme.bg.fill}`}
                    width="20"
                    height="20"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="relative">
              <label
                htmlFor="event-description"
                className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
              >
                Description
              </label>
              <div className="flex">
                <div className="w-full">
                  <Editor
                    initialValue={description}
                    onChange={setDescription}
                    placeholder="Enter event description"
                    disabled={isSubmitting}
                  />
                </div>
                <button
                  type="button"
                  className="ml-2 p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 self-start"
                  onClick={() => {
                    setEmojiTarget("description");
                    setShowEmojiPicker(!showEmojiPicker);
                  }}
                >
                  <svg
                    className={`${theme.bg.fill}`}
                    width="20"
                    height="20"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputControl
                id="start-date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isSubmitting}
                type="date"
                error={error && !startDate ? "Start date is required" : null}
                required
              />
              <InputControl
                id="start-time"
                label="Start Time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={isSubmitting}
                type="time"
                error={error && !startTime ? "Start time is required" : null}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputControl
                id="end-date"
                label="End Date (Optional)"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isSubmitting}
                type="date"
              />
              <InputControl
                id="end-time"
                label="End Time (Optional)"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isSubmitting}
                type="time"
              />
            </div>

            <SelectControl
              id="category"
              label="Category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
              placeholder="Select a category"
              disabled={isSubmitting}
              error={error && !categoryId ? "Category is required" : null}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectControl
                id="event-type"
                label="Event Format"
                value={eventType}
                onChange={(e) => {
                  const value = e.target.value as EventType;
                  setEventType(value);
                  if (value === "virtual") {
                    setLocation("");
                  }
                }}
                options={EVENT_TYPE_OPTIONS}
                disabled={isSubmitting}
              />
              <SelectControl
                id="timezone"
                label="Timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                options={TIMEZONE_OPTIONS}
                disabled={isSubmitting}
              />
            </div>

            {eventType !== "virtual" && (
              <InputControl
                id="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. 123 Main St, New York, NY"
                disabled={isSubmitting}
              />
            )}

            {eventType !== "in-person" && (
              <InputControl
                id="url"
                label="Meeting URL (Optional)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://zoom.us/j/123456789"
                disabled={isSubmitting}
                type="url"
              />
            )}

            {eventType === "in-person" && (
              <InputControl
                id="url"
                label="URL (Optional)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                disabled={isSubmitting}
                type="url"
              />
            )}

            {/* Attachments */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Attachments
                </label>
                <button
                  type="button"
                  className={`text-sm ${theme.text.brand} ${theme.text.brandHover}`}
                  onClick={() =>
                    setShowMediaUploaderAttachments(
                      !showMediaUploaderAttachments
                    )
                  }
                  disabled={isSubmitting}
                >
                  {showMediaUploaderAttachments ? "Cancel" : "Add attachments"}
                </button>
              </div>

              {/* Media Uploader */}
              {showMediaUploaderAttachments && (
                <div className="mb-3">
                  <MediaUploader
                    onUploadComplete={handleMediaUploadAttachments}
                    onError={(error) => {
                      toast.error(error);
                      setShowMediaUploaderAttachments(false);
                    }}
                  />
                </div>
              )}

              {/* Media preview */}
              {mediaFilesAttachments.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-2">
                  {mediaFilesAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700"
                    >
                      {file.type === MediaType.IMAGE ? (
                        <img
                          src={file.uploadedUrl || file.preview}
                          alt=""
                          className="w-full h-auto object-cover"
                        />
                      ) : (
                        <video
                          src={file.uploadedUrl || file.preview}
                          className="w-full h-auto object-cover"
                          controls
                        />
                      )}
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                        onClick={() => {
                          setMediaFilesAttachments((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                        }}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                      {file.progress < 100 && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                          <div
                            className={`h-full ${theme.bg.brand}`}
                            style={{ width: `${file.progress}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

            <div className="flex justify-between mt-4">
              <div>
                {event && onDelete && canDelete && (
                  <Button
                    type="button"
                    onClick={handleDelete}
                    variant="error"
                    disabled={isSubmitting}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || !canEdit}>
                  {isSubmitting ? "Saving..." : event ? "Update" : "Create"}
                </Button>
              </div>
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div
                className={`absolute z-50 right-10 mt-2 ${emojiTarget === "title" ? "bottom-14" : "bottom-[-4rem]"
                  }`}
              >
                <EmojiPicker
                  height={450}
                  width={350}
                  onEmojiClick={(emojiData) => handleEmojiClick(emojiData)}
                />
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
